
import { Injectable } from '@angular/core';

import { filter, map, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';

import { MediaTypeEnum } from '../../Basic/enums/media-type.enum';
import { MonitoringAccountConfigurationApi } from '../../../api/monitoring/monitoring-account-configuration.api';
import { MercuryApi } from '../../../api/mercury/mercury-api';
import { MonitoringAccountConfigurationApiModel, MentionSavingQuota } from '../../../api/monitoring/model/monitoring-account-configuration-api.model';

import { LanguageModel } from '../../../Core/Localization/models/language.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';
import { gateCall } from '../../../Core/rxjs-utils';
import { availableLanguages } from '../../../Core/Localization/models/available-languages.model';
import { SearchRangeModel } from '../../../Monitor/Core/models/search-range.model';
import { MentionImportTypesEnum } from '../enum/mention-import-types.enum';
@Injectable()
export class MonitorConfigService {
    private configModel: MonitoringAccountConfigurationApiModel;
    private configModel$: BehaviorSubject<MonitoringAccountConfigurationApiModel> = new BehaviorSubject<MonitoringAccountConfigurationApiModel>(null);

    private availableLanguages: LanguageModel[] = availableLanguages;
    private availableLanguages$: BehaviorSubject<LanguageModel[]> = new BehaviorSubject<LanguageModel[]>(this.availableLanguages);

    private searchRange: SearchRangeModel = null;
    private searchRange$: BehaviorSubject<SearchRangeModel> = new BehaviorSubject<SearchRangeModel>(this.searchRange);
    private searchRangeSubscription: Subscription;

    private refreshConfig$ = new Subject<void>();

    private subscriptions: Subscription[] = [];

    constructor(
        private monitoringAccountApi: MonitoringAccountConfigurationApi,
        private mercuryApi: MercuryApi,
        private currentUserStateService: CurrentUserStateService
    ) {
        this.subscriptions.push(
            this.refreshConfig$.pipe(
                switchMap(() => gateCall(this.hasMonitoringConfigAccess())),
                switchMap(() => this.monitoringAccountApi.getMonitoringAccountConfigurationModel()),
                tap(configModel => {
                    this.setConfig(configModel);
                })
            ).subscribe()
        );

        this.refreshMonitoringConfigModel();
    }

    getConfig(): Observable<MonitoringAccountConfigurationApiModel> {
        return this.configModel$;
    }

    private setConfig(config: MonitoringAccountConfigurationApiModel) {
        this.configModel = config;
        this.configModel$.next(this.configModel);
    }

    /**
     * Refreshes the monitoring config model
     */
    refreshMonitoringConfigModel(): void {
        this.refreshConfig$.next();
    }

    /**
     * Gets available media types for a user to perform a topic search on
     */
    getAvailableMediaTypes(): Observable<MediaTypeEnum[]> {
        return this.configModel$.pipe(
            map(config => {
                if (!config) {
                    return [];
                }

                // TODO: NEP-12686 - Remove this line and use getAvailableSocialMediaTypes in all places where we need
                // to check social media types
                // HACK: to include tiktok, linkedin & bluesky in the app now that it has been moved exclusively to socialMediaTypes
                if (config.socialMediaTypes.includes(MediaTypeEnum.TIKTOK)
                    && !config.mediaTypes.includes(MediaTypeEnum.TIKTOK)
                ){
                    config.mediaTypes.push(MediaTypeEnum.TIKTOK);
                }

                if (config.socialMediaTypes.includes(MediaTypeEnum.LINKEDIN)
                    && !config.mediaTypes.includes(MediaTypeEnum.LINKEDIN)
                ){
                    config.mediaTypes.push(MediaTypeEnum.LINKEDIN);
                }

                if (config.socialMediaTypes.includes(MediaTypeEnum.BLUESKY)
                    && !config.mediaTypes.includes(MediaTypeEnum.BLUESKY)
                ){
                    config.mediaTypes.push(MediaTypeEnum.BLUESKY);
                }

                return config.mediaTypes;
            })
        );
    }

    /**
     * Gets available social media types for a user to use for Social queries
     */
    getAvailableSocialMediaTypes(): Observable<MediaTypeEnum[]> {
        return this.configModel$.pipe(
            map(config => {
                if (!config) {
                    return [];
                }

                return config.socialMediaTypes;
            })
        );
    }

    /**
     * Gets number of tagging topics provisioned on current account
     */
    getTaggingTopicLimit(): Observable<number> {
        return this.configModel$.pipe(map(config => {
            if (!config) {
                return 25;
            }

            return config.taggingSearchLimit;
        }));
    }

    /**
     * Gets number of auto saving topics limit provisioned on current account
     */
    getAutoSavingTopicLimit(): Observable<number> {
        return this.configModel$.pipe(map(config => {
            if (!config) {
                return null;
            }
            return config.autoSaveTopicLimit;
        }));
    }


    /**
     * Gets the list of available languages as an observable, this function probably needs a better home
     */
    getAvailableLanguages(): Observable<LanguageModel[]> {
        return this.availableLanguages$;
    }

    getSearchRange(): Observable<SearchRangeModel> {
        if (!this.searchRange && !this.searchRangeSubscription) {
            this.searchRangeSubscription = this.mercuryApi.searchRanges().subscribe(
                ranges => {
                    this.searchRange = new SearchRangeModel(ranges);
                    this.searchRange$.next(this.searchRange);
                }
            );
        }
        return this.searchRange$;
    }

    getTvEyesEnabled(): Observable<boolean> {
        return this.configModel$.pipe(
            filter(x => !!x),
            map(x => !!x.hasTveyes)
        );
    }

    getTvEyesArchivingEnabled(): Observable<boolean> {
        return this.configModel$.pipe(
            filter(x => !!x),
            map(x => !!x.tveyes?.isArchiveProvisioned || !!x.hasTveyesArchiving)
        );
    }

    getHasMentionInsightsEnabled(): Observable<boolean> {
        return this.configModel$.pipe(
            filter(x => !!x),
            map(x => !!x.hasMentionInsights)
        );
    }

    getHasCoverageInsightsEnabled(): Observable<boolean> {
        return this.configModel$.pipe(
            filter(x => !!x),
            map(x => !!x.hasCoverageInsights)
        );
    }

    getCriticalMentionEnabled(): Observable<boolean> {
        return this.configModel$.pipe(
            filter(x => !!x),
            map(x => x.hasCriticalMention)
        );
    }

    getFinancialTimesEnabled(): Observable<boolean> {
        return this.configModel$.pipe(
            filter(x => !!x),
            map(x => x.hasFinancialTimes)
        );
    }

    getFinancialPodcastOnlyEnabled(): Observable<boolean> {
        return this.configModel$.pipe(
            filter(x => !!x),
            map(x => x.hasTVEyesFinancialPodcastOnly)
        );
    }

    getProminenceProvision(): Observable<number> {
        return this.configModel$.pipe(
            filter(x => !!x),
            map(x => x.prominenceGroupLimit)
        );
    }

    getUnsavedMentionExportLimit(): Observable<number> {
        return this.configModel$.pipe(
            filter(x => !!x),
            map(x => x.unsavedMentionExportLimit)
        );
    }

    getMentionImportTypes(): Observable<MentionImportTypesEnum[]> {
        return this.configModel$.pipe(
            filter(x => !!x),
            map(x => x.mentionImportTypes)
        );
    }

    getMentionSavingQuota(): Observable<MentionSavingQuota> {
        return this.configModel$.pipe(
            filter(x => !!x),
            map(config => {
                const quota: MentionSavingQuota = {
                    savingQuota: config.savingQuota ?? -1,
                    additionalMentionQuota: config.additionalMentionQuota ?? 0,
                    savingQuotaUsed: config.savingQuotaUsed,
                    savingQuotaStart: config.savingQuotaStart,
                    savingQuotaEnd: config.savingQuotaEnd
                };
                quota.savingQuotaRemaining = quota.savingQuota === -1 ? -1 : (quota.savingQuota + quota.additionalMentionQuota) - quota.savingQuotaUsed;
                return quota;
            })
        );
    }

    /**
     * Observable completes after call, via {@link CurrentUserStateService.hasAnyPermissions}
     */
    hasMonitoringConfigAccess(): Observable<boolean> {
        const configPermissions = [
            userPermissionsEnum.advancedMonitoring,
            userPermissionsEnum.socialIntelligence
        ];
        return this.currentUserStateService.hasAnyPermissions(configPermissions);
    }

    hasSocialConfigAccess(): Observable<boolean> {
        return this.currentUserStateService.hasAnyPermissions([userPermissionsEnum.socialIntelligence]);
    }

    hasSubscriptions(): Observable<boolean> {
        return this.currentUserStateService.hasAnyPermissions([userPermissionsEnum.mediaSubscriptions]);
    }
}
